import { api } from '@/lib/customFetch'
import { tryRefreshToken } from '@/lib/refreshInterceptor'
import type {
  DocumentSummary,
  DocumentDetail,
  DocumentVersion,
  PagedList,
  ViewStatus,
  ListDocumentsParams,
} from '@/lib/types/ged'

export const documentKeys = {
  list: (params: ListDocumentsParams) => ['documents', params] as const,
  detail: (id: string) => ['documents', id] as const,
  versions: (id: string) => ['documents', id, 'versions'] as const,
  viewStatus: (id: string) => ['documents', id, 'view-status'] as const,
}

function buildQueryString(params: ListDocumentsParams): string {
  const parts: string[] = []
  if (params.areaId) parts.push(`areaId=${encodeURIComponent(params.areaId)}`)
  if (params.typeId) parts.push(`typeId=${encodeURIComponent(params.typeId)}`)
  if (params.search) parts.push(`search=${encodeURIComponent(params.search)}`)
  if (params.dateFrom) parts.push(`dateFrom=${encodeURIComponent(params.dateFrom)}`)
  if (params.dateTo) parts.push(`dateTo=${encodeURIComponent(params.dateTo)}`)
  if (params.page !== undefined) parts.push(`page=${params.page}`)
  if (params.pageSize !== undefined) parts.push(`pageSize=${params.pageSize}`)
  return parts.length ? `?${parts.join('&')}` : ''
}

export async function listDocuments(
  params: ListDocumentsParams,
): Promise<PagedList<DocumentSummary>> {
  return api.get<PagedList<DocumentSummary>>(`/documents${buildQueryString(params)}`)
}

export async function getDocument(id: string): Promise<DocumentDetail> {
  return api.get<DocumentDetail>(`/documents/${id}`)
}

export async function downloadDocument(id: string): Promise<Blob> {
  // Não passa pelo customFetch (que assume body JSON). Replica o retry-on-401:
  // se o cookie JWT expirou, refresh + retry uma vez antes de propagar o erro.
  // Bug anterior: 1ª tentativa falhava com 401, 2ª funcionava porque um request
  // paralelo (ex.: markDocumentViewed via api.post) tinha refresh-ado o cookie.
  const url = `/api/documents/${id}/download`
  let response = await fetch(url, { credentials: 'include' })
  if (response.status === 401) {
    const refreshed = await tryRefreshToken()
    if (refreshed) response = await fetch(url, { credentials: 'include' })
  }
  if (!response.ok) throw new Error(`Download failed: ${response.status}`)
  return response.blob()
}

// Side-effect fire-and-forget: registrar visualização/download é um tracking
// pessoal e a falha (ex.: 404 quando o admin abre doc em área sem perm
// explícita — vide MarkDocumentViewedHandler que NÃO aceita admin bypass)
// não deve gerar toast vermelho pro usuário. Calls passam silent:true.
export async function markDocumentViewed(id: string): Promise<void> {
  return api.post<void>(`/documents/${id}/views`, undefined, { silent: true })
}

export async function markDocumentDownloaded(id: string): Promise<void> {
  return api.post<void>(`/documents/${id}/downloads`, undefined, { silent: true })
}

export async function getViewStatus(id: string): Promise<ViewStatus> {
  return api.get<ViewStatus>(`/documents/${id}/view-status`)
}

export async function listDocumentVersions(id: string): Promise<DocumentVersion[]> {
  return api.get<DocumentVersion[]>(`/documents/${id}/versions`)
}

export type UploadDocumentInput = {
  areaId: string; typeId: string; name: string
  description?: string; documentDate: string; file: File
}
export type UpdateDocumentInput = {
  name: string; description?: string; documentDate: string
  areaId: string; typeId: string
}

export async function uploadDocument(input: UploadDocumentInput): Promise<DocumentDetail> {
  const fd = new FormData()
  fd.append('file', input.file)
  fd.append('areaId', input.areaId)
  fd.append('typeId', input.typeId)
  fd.append('name', input.name)
  fd.append('documentDate', input.documentDate)
  if (input.description) fd.append('description', input.description)
  const res = await fetch('/api/documents', { method: 'POST', body: fd, credentials: 'include' })
  if (!res.ok) throw new Error(await res.text())
  return res.json()
}

export async function updateDocument(id: string, data: UpdateDocumentInput): Promise<DocumentDetail> {
  return api.patch<DocumentDetail>(`/documents/${id}`, data)
}

export async function deleteDocument(id: string): Promise<void> {
  await api.delete(`/documents/${id}`)
}

export async function uploadDocumentVersion(id: string, file: File, notes?: string): Promise<DocumentVersion> {
  const fd = new FormData()
  fd.append('file', file)
  if (notes) fd.append('notes', notes)
  const res = await fetch(`/api/documents/${id}/versions`, { method: 'POST', body: fd, credentials: 'include' })
  if (!res.ok) throw new Error(await res.text())
  return res.json()
}

// Extrai mensagem amigável de uma resposta de erro ASP.NET (ProblemDetails),
// já que XHR não passa pelo customFetch e perderia os campos errors[].
function extractProblemMessage(responseText: string, status: number): string {
  try {
    const body = JSON.parse(responseText) as {
      title?: string
      detail?: string
      errors?: Record<string, string[]>
    }
    if (body.errors) {
      const all = Object.values(body.errors).flat().filter(Boolean)
      if (all.length > 0) return all.join(' ')
    }
    if (body.detail) return body.detail
    if (body.title) return body.title
  } catch {
    /* não-JSON; cai no fallback */
  }
  return `Falha no envio (HTTP ${status}).`
}

export function uploadDocumentVersionWithProgress(
  id: string,
  file: File,
  notes: string | undefined,
  onProgress: (loaded: number, total: number) => void,
): Promise<DocumentVersion> {
  return new Promise<DocumentVersion>((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    const fd = new FormData()
    fd.append('file', file)
    if (notes) fd.append('notes', notes)

    xhr.upload.addEventListener('progress', (e: ProgressEvent) => {
      if (e.lengthComputable) onProgress(e.loaded, e.total)
    })

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try { resolve(JSON.parse(xhr.responseText) as DocumentVersion) }
        catch { reject(new Error('Invalid response JSON')) }
      } else {
        reject(new Error(extractProblemMessage(xhr.responseText, xhr.status)))
      }
    })

    xhr.addEventListener('error', () => reject(new Error('Network error')))
    xhr.open('POST', `/api/documents/${id}/versions`)
    xhr.withCredentials = true
    xhr.send(fd)
  })
}

export function uploadDocumentWithProgress(
  input: UploadDocumentInput,
  onProgress: (loaded: number, total: number) => void,
  signal?: AbortSignal,
): Promise<DocumentDetail> {
  return new Promise<DocumentDetail>((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    const fd = new FormData()
    fd.append('file', input.file)
    fd.append('areaId', input.areaId)
    fd.append('typeId', input.typeId)
    fd.append('name', input.name)
    fd.append('documentDate', input.documentDate)
    if (input.description) fd.append('description', input.description)

    xhr.upload.addEventListener('progress', (e: ProgressEvent) => {
      if (e.lengthComputable) onProgress(e.loaded, e.total)
    })

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try { resolve(JSON.parse(xhr.responseText) as DocumentDetail) }
        catch { reject(new Error('Invalid response JSON')) }
      } else {
        reject(new Error(extractProblemMessage(xhr.responseText, xhr.status)))
      }
    })

    xhr.addEventListener('error', () => reject(new Error('Network error')))
    xhr.addEventListener('abort', () => reject(new DOMException('Aborted', 'AbortError')))

    signal?.addEventListener('abort', () => xhr.abort())

    xhr.open('POST', '/api/documents')
    xhr.withCredentials = true
    xhr.send(fd)
  })
}
